/**
 * OnboardingPage — fluxo guiado pós-cadastro.
 *
 * 3 passos: (1) Boas-vindas, (2) Adicionar primeiro veículo, (3) Escolher plano.
 * O cliente pode pular passos 2 e 3 e voltar depois. Cada passo é uma seção
 * dentro de uma single-page com transição motion entre eles.
 *
 * Roteamento: /onboarding. Acionado via redirect do signup-page após o
 * cliente confirmar e-mail (logar pela primeira vez), ou manualmente
 * via link "começar tour" no perfil.
 *
 * Não força permanência: o cliente pode ir pra qualquer rota do app via
 * bottom nav após chegar no passo 3.
 */
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  CarFront,
  CheckCircle2,
  PartyPopper,
  Sparkles,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

// Cast — ind_onboarding_completo entra em database.types.ts apos
// `npm run db:types` rodar pos migration 20260522100000.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb = supabase as any;

/**
 * Marca ind_onboarding_completo = TRUE no cliente atual. Best-effort:
 * qualquer falha vira toast warning mas nao bloqueia a navegacao.
 */
async function marcarOnboardingCompleto(authUserId: string | undefined): Promise<void> {
  if (!authUserId) return;
  try {
    await sb
      .from('tab_clientes')
      .update({ ind_onboarding_completo: true })
      .eq('cod_auth_user', authUserId);
  } catch {
    // best-effort — proximo render do hook useEnsureOnboarding tenta de novo
  }
}

type Passo = 1 | 2 | 3;
const TOTAL = 3;

interface StepMeta {
  num: Passo;
  titulo: string;
  desc: string;
}
const STEPS: StepMeta[] = [
  { num: 1, titulo: 'Boas-vindas', desc: 'Te apresentando' },
  { num: 2, titulo: 'Seu veículo', desc: 'Cadastrar primeiro carro' },
  { num: 3, titulo: 'Tudo pronto', desc: 'Hora de lavar' },
];

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [passo, setPasso] = useState<Passo>(1);

  function avancar() {
    if (passo < TOTAL) setPasso((passo + 1) as Passo);
  }
  function voltar() {
    if (passo > 1) setPasso((passo - 1) as Passo);
  }

  /**
   * Pular passo 2 (veiculo): marca onboarding como completo e avanca.
   * Sem onboarding parcial — a decisao de pular e definitiva no sentido
   * de NAO trazer o cliente de volta pra ca em proximos logins.
   */
  async function pularVeiculo() {
    await marcarOnboardingCompleto(user?.id);
    avancar();
  }

  /**
   * Conclusao do passo 3: marca flag, mostra toast, vai pra home (ou planos
   * se o cliente clicou no CTA Ver planos — esse caso e tratado inline).
   */
  async function concluir() {
    await marcarOnboardingCompleto(user?.id);
    toast.success('Tudo pronto!', { description: 'Bem-vindo a bordo.' });
    navigate('/', { replace: true });
  }

  /**
   * Mesma marcacao mas redireciona pra /planos (clicar em Ver planos no passo 3).
   */
  async function concluirEVerPlanos() {
    await marcarOnboardingCompleto(user?.id);
    navigate('/planos', { replace: true });
  }

  return (
    <div className="bg-octa-hero relative flex min-h-screen flex-col overflow-hidden">
      {/* Blobs decorativos */}
      <motion.div
        aria-hidden
        className="octa-blob bg-octa-gradient pointer-events-none absolute -left-32 -top-32 h-[26rem] w-[26rem]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ duration: 1.2 }}
      />
      <motion.div
        aria-hidden
        className="octa-blob bg-octa-accent pointer-events-none absolute -bottom-32 -right-32 h-[24rem] w-[24rem]"
        style={{ animationDelay: '-4s' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ duration: 1.2, delay: 0.15 }}
      />

      {/* Stepper horizontal */}
      <div className="relative z-10 mx-auto w-full max-w-md px-4 pb-2 pt-6">
        <ol className="flex items-center gap-2" aria-label="Progresso do onboarding">
          {STEPS.map((s, i) => {
            const completo = s.num < passo;
            const ativo = s.num === passo;
            return (
              <li key={s.num} className="flex flex-1 items-center gap-2">
                <div className="flex flex-1 flex-col items-center gap-1">
                  <div
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all',
                      completo && 'bg-octa-gradient shadow-octa-glow text-white',
                      ativo &&
                        !completo &&
                        'bg-octa-gradient shadow-octa-glow scale-110 text-white',
                      !completo && !ativo && 'bg-muted text-muted-foreground',
                    )}
                  >
                    {completo ? <CheckCircle2 className="h-4 w-4" /> : s.num}
                  </div>
                  <span
                    className={cn(
                      'text-[10px] font-semibold uppercase tracking-wider transition-colors',
                      ativo || completo ? 'text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {s.titulo}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    className={cn(
                      '-mt-4 h-0.5 flex-1 rounded-full transition-colors',
                      completo ? 'bg-octa-gradient' : 'bg-muted',
                    )}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Conteúdo do passo */}
      <main className="relative z-10 flex flex-1 items-center justify-center px-4 py-6">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            {passo === 1 && <PassoBoasVindas key="1" onContinuar={avancar} />}
            {passo === 2 && <PassoVeiculo key="2" onContinuar={avancar} onPular={pularVeiculo} />}
            {passo === 3 && (
              <PassoConcluir key="3" onConcluir={concluir} onVerPlanos={concluirEVerPlanos} />
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Voltar inferior (passos > 1, exceto o final) */}
      {passo > 1 && passo < TOTAL && (
        <motion.div
          className="relative z-10 mx-auto w-full max-w-md px-4 pb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <button
            type="button"
            onClick={voltar}
            className="text-muted-foreground hover:text-foreground octa-tap mx-auto flex items-center gap-1.5 text-xs font-semibold"
          >
            <ArrowLeft className="h-3 w-3" />
            Voltar
          </button>
        </motion.div>
      )}
    </div>
  );
}

// ============================================================================
// Passo 1 — Boas-vindas
// ============================================================================
function PassoBoasVindas({ onContinuar }: { onContinuar: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="text-center"
    >
      <motion.div
        className="bg-octa-gradient shadow-octa-glow octa-float mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-3xl ring-4 ring-white/40"
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.2, duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
      >
        <Sparkles className="h-12 w-12 text-white" />
      </motion.div>
      <motion.h1
        className="text-display-lg font-extrabold tracking-tight"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
      >
        Bem-vindo a bordo
      </motion.h1>
      <motion.p
        className="text-muted-foreground mx-auto mt-3 max-w-xs text-sm leading-relaxed"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
      >
        Seu carro lavado quantas vezes você quiser, sem fila, sem cartão na hora. Em 2 minutos a
        gente te deixa pronto.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
      >
        <Button
          variant="accent"
          size="hero"
          onClick={onContinuar}
          className="mt-8 w-full rounded-2xl"
        >
          Vamos comecar
          <ArrowRight className="h-4 w-4" />
        </Button>
      </motion.div>
    </motion.div>
  );
}

// ============================================================================
// Passo 2 — Adicionar primeiro veículo
// ============================================================================
function PassoVeiculo({ onContinuar, onPular }: { onContinuar: () => void; onPular: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="text-center"
    >
      <Card variant="glass" className="rounded-3xl border-white/40">
        <CardContent className="space-y-5 p-7">
          <motion.div
            className="bg-octa-gradient shadow-octa-glow mx-auto flex h-20 w-20 items-center justify-center rounded-3xl text-white ring-4 ring-white/40"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.15, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <CarFront className="h-10 w-10" />
          </motion.div>
          <div className="space-y-2">
            <h2 className="text-display-sm font-extrabold tracking-tight">Cadastre seu carro</h2>
            <p className="text-muted-foreground mx-auto max-w-xs text-sm leading-relaxed">
              Vamos precisar da placa, marca e modelo. É o seu carro que vai ser identificado no
              box.
            </p>
          </div>

          <ul className="octa-stagger space-y-2 text-left">
            <BulletItem texto="Placa Mercosul ou antiga — a gente identifica" />
            <BulletItem texto="Marca e modelo direto da nossa base" />
            <BulletItem texto="Você pode mudar a placa depois, com cautela" />
          </ul>

          <div className="space-y-2 pt-1">
            <Link to="/veiculos/adicionar?onboarding=1" onClick={onContinuar}>
              <Button variant="accent" size="lg" className="h-12 w-full rounded-xl font-bold">
                Adicionar veículo
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Button variant="ghost" onClick={onPular} className="h-10 w-full rounded-xl text-xs">
              Pular por agora
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function BulletItem({ texto }: { texto: string }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <CheckCircle2 className="text-primary mt-0.5 h-4 w-4 shrink-0" />
      <span className="text-foreground">{texto}</span>
    </li>
  );
}

// ============================================================================
// Passo 3 — Concluir (com confetti + CTA pra escolher plano)
// ============================================================================
function PassoConcluir({
  onConcluir,
  onVerPlanos,
}: {
  onConcluir: () => void;
  onVerPlanos: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="text-center"
    >
      <motion.div
        className="bg-octa-gradient shadow-octa-glow octa-confetti relative mx-auto mb-6 flex h-28 w-28 items-center justify-center rounded-full ring-4 ring-white/40"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
      >
        <PartyPopper className="h-14 w-14 text-white" />
      </motion.div>
      <motion.h2
        className="text-display-lg font-extrabold tracking-tight"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        Tudo pronto
      </motion.h2>
      <motion.p
        className="text-muted-foreground mx-auto mt-3 max-w-xs text-sm leading-relaxed"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        Que tal escolher um plano agora? Se preferir, dá pra explorar o app primeiro e decidir
        depois.
      </motion.p>

      <motion.div
        className="mt-7 space-y-2"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <Button variant="accent" size="hero" onClick={onVerPlanos} className="w-full rounded-2xl">
          <Wallet className="h-4 w-4" />
          Ver planos
        </Button>
        <Button variant="ghost" onClick={onConcluir} className="h-11 w-full rounded-xl text-xs">
          Explorar o app primeiro
        </Button>
      </motion.div>
    </motion.div>
  );
}
